import React, { useRef, useEffect, useCallback } from "react";

/**
 * SandRevealImg
 *
 * Overlays a canvas on an <img>.
 * 500 sand-grain particles fly in from below-left at 45° and
 * "build up" the image over ~2 seconds.
 *
 * Props:
 *   src            – image URL
 *   alt            – alt text
 *   imgClassName   – CSS class for the <img>
 *   size           – fallback size in px if layout not measured (default 90)
 *   triggerOnMount – if true, starts immediately on mount (hero avatar)
 *                    otherwise waits for IntersectionObserver
 */
function SandRevealImg({ src, alt, imgClassName, size = 90, triggerOnMount = false }) {
  const canvasRef  = useRef(null);
  const imgRef     = useRef(null);
  const startedRef = useRef(false);
  const animRef    = useRef(null);

  /* ── core particle animation — runs after image is measured ── */
  const runAnimation = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const canvas = canvasRef.current;
    const img    = imgRef.current;
    if (!canvas || !img) return;

    /* Keep retrying until the image has real layout dimensions */
    const tryStart = () => {
      const rect = img.getBoundingClientRect();
      const W = Math.round(rect.width);
      const H = Math.round(rect.height);

      if (W === 0 || H === 0) {
        animRef.current = requestAnimationFrame(tryStart);
        return;
      }

      /* Set canvas pixel buffer and CSS display size to match image exactly */
      canvas.width        = W;
      canvas.height       = H;
      canvas.style.width  = W + "px";
      canvas.style.height = H + "px";

      const ctx = canvas.getContext("2d");

      /* Fill opaque — covers the image underneath */
      ctx.fillStyle = "#08080e";
      ctx.fillRect(0, 0, W, H);

      /* ── particle system ── */
      const NUM          = 500;
      const DELAY_SPREAD = 0.65;
      const ANGLE        = Math.PI / 4;   // 45°
      const COS_A        = Math.cos(ANGLE);
      const SIN_A        = Math.sin(ANGLE);

      const particles = Array.from({ length: NUM }, () => {
        const endX  = Math.random() * W;
        const endY  = Math.random() * H;
        const dist  = (1.0 + Math.random() * 0.7) * Math.max(W, H);
        return {
          startX: endX - COS_A * dist,
          startY: endY + SIN_A * dist,
          endX, endY,
          r:     0.3 + Math.random() * Math.random() * (Math.max(W, H) / 40),
          delay: Math.random() * DELAY_SPREAD,
        };
      });

      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const DURATION  = 1100;
      const startTime = performance.now();

      const frame = (now) => {
        const elapsed  = now - startTime;
        const progress = Math.min(elapsed / DURATION, 1);

        /* Reset opaque overlay */
        ctx.globalCompositeOperation = "source-over";
        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = "#08080e";
        ctx.fillRect(0, 0, W, H);

        /* Punch transparent holes so image shows through */
        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = "rgba(0,0,0,1)";

        for (const p of particles) {
          const local = (progress - p.delay) / (1 - p.delay + 1e-6);
          if (local <= 0) continue;
          const t = easeOutCubic(Math.min(local, 1));
          const x = p.startX + (p.endX - p.startX) * t;
          const y = p.startY + (p.endY - p.startY) * t;
          ctx.beginPath();
          ctx.arc(x, y, p.r, 0, Math.PI * 2);
          ctx.fill();
        }

        if (progress < 1) {
          animRef.current = requestAnimationFrame(frame);
        } else {
          /* Fade out the remaining overlay */
          let alpha = 1;
          const fadeOut = () => {
            alpha -= 0.055;
            if (!canvas) return;
            canvas.style.opacity = String(Math.max(alpha, 0));
            if (alpha > 0) {
              animRef.current = requestAnimationFrame(fadeOut);
            } else {
              canvas.style.display = "none";
            }
          };
          animRef.current = requestAnimationFrame(fadeOut);
        }
      };

      animRef.current = requestAnimationFrame(frame);
    };

    /* If image not yet loaded, wait for it first */
    if (img.complete) {
      animRef.current = requestAnimationFrame(tryStart);
    } else {
      img.addEventListener("load", () => {
        animRef.current = requestAnimationFrame(tryStart);
      }, { once: true });
    }
  }, []); // no deps — reads everything from refs at call time

  /* ── trigger logic ── */
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    if (triggerOnMount) {
      /* Small delay so React has committed and CSS has applied */
      const t = setTimeout(runAnimation, 100);
      return () => {
        clearTimeout(t);
        if (animRef.current) cancelAnimationFrame(animRef.current);
      };
    }

    /* Scroll-triggered: IntersectionObserver */
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          runAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(img);

    return () => {
      observer.disconnect();
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [runAnimation, triggerOnMount]);

  return (
    <div className="sand-reveal-wrap">
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={imgClassName}
      />
      <canvas
        ref={canvasRef}
        className="sand-reveal-canvas"
        aria-hidden="true"
      />
    </div>
  );
}

export default SandRevealImg;
